const PRODUCTS_QUERY = `
  query CableGuideProducts($handle: String!, $cursor: String) {
    collection(handle: $handle) {
      products(first: 250, after: $cursor) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          handle
          title
          vendor
          featuredImage {
            url(transform: { maxWidth: 720, maxHeight: 720 })
            altText
            width
            height
          }
          images(first: 2) {
            nodes {
              url(transform: { maxWidth: 720, maxHeight: 720 })
              altText
              width
              height
            }
          }
          variants(first: 100) {
            nodes {
              id
              title
              availableForSale
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              image {
                url(transform: { maxWidth: 720, maxHeight: 720 })
                altText
                width
                height
              }
            }
          }
          subtitle: metafield(namespace: "descriptors", key: "subtitle") {
            value
          }
          reviewRating: metafield(namespace: "reviews", key: "rating") {
            value
          }
          reviewRatingCount: metafield(namespace: "reviews", key: "rating_count") {
            value
          }
          inputConnection: metafield(namespace: "guide", key: "input_connection") {
            value
          }
          outputConnection: metafield(namespace: "guide", key: "output_connection") {
            value
          }
          cableBidirectional: metafield(namespace: "guide", key: "cable_bidirectional") {
            value
          }
        }
      }
    }
  }
`;

if (!customElements.get('cable-guide-section')) {
  customElements.define(
    'cable-guide-section',
    class CableGuideSection extends HTMLElement {
      connectedCallback() {
        this.inputBtns = this.querySelectorAll('[data-cable-input-btn]');
        this.outputBtns = this.querySelectorAll('[data-cable-output-btn]');
        this.outputStep = this.querySelector('[data-cable-step="output"]');
        this.filterEl = this.querySelector('[data-cable-filter]');
        this.actionsEl = this.querySelector('[data-cable-actions]');
        this.resultsEl = this.querySelector('[data-cable-results]');
        this.productGrid = this.querySelector('[data-cable-product-grid]');
        this.noResultsEl = this.querySelector('[data-cable-no-results]');
        this.loadingEl = this.querySelector('[data-cable-loading]');
        this.errorEl = this.querySelector('[data-cable-error]');
        this.directionalNoteEl = this.querySelector('[data-cable-directional-note]');
        this.cardColorScheme = this.dataset.cardColorScheme || 'scheme-1';
        this.cardStyle = this.dataset.cardStyle || 'standard';
        this.cardTextAlignment = this.dataset.cardTextAlignment || 'left';
        this.cardTitleClass = this.dataset.cardTitleClass || '';
        this.cardTitleTransform = this.dataset.cardTitleTransform || '';
        this.cardPriceClass = this.dataset.cardPriceClass || '';
        this.cardImageRatio = this.dataset.cardImageRatio || 'adapt';
        this.cardImageCover = this.dataset.cardImageCover === 'true';
        this.cardImagePosition = this.dataset.cardImagePosition || 'center';
        this.cardShowSecondaryImage = this.dataset.cardShowSecondaryImage === 'true';
        this.cardShowVendor = this.dataset.cardShowVendor === 'true';
        this.cardShowSubtitle = this.dataset.cardShowSubtitle === 'true';
        this.cardShowRating = this.dataset.cardShowRating === 'true';
        this.cardStarSize = parseFloat(this.dataset.cardStarSize) || 1;
        this.cardEnableQuickAdd = this.dataset.cardEnableQuickAdd === 'true';
        this.directionalLabel = this.dataset.directionalLabel || 'Directional';
        this.badgeShape = this.dataset.badgeShape || 'default';
        this.addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';
        this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
        this.saleLabel = this.dataset.saleLabel || 'Sale';
        this.regularPriceLabel = this.dataset.regularPriceLabel || 'Regular price';
        this.salePriceLabel = this.dataset.salePriceLabel || 'Sale price';
        this.addedLabel = this.dataset.addedLabel || 'Added';
        this.cartErrorLabel = this.dataset.cartErrorLabel || 'Error — try again';

        this.selectedInput = null;
        this.selectedOutput = null;
        this.products = [];

        this.inputBtns.forEach((btn) => btn.addEventListener('click', () => this.selectInput(btn)));
        this.outputBtns.forEach((btn) => btn.addEventListener('click', () => this.selectOutput(btn)));
        this.querySelector('[data-cable-reset]')?.addEventListener('click', () => this.reset());
        this.productGrid.addEventListener('click', (event) => {
          const button = event.target.closest('[data-cable-atc]');
          if (button) this.addToCart(button);
        });

        this.init();
      }

      async init() {
        const { storefrontToken, shopDomain, collectionHandle } = this.dataset;

        if (!storefrontToken || !shopDomain || !collectionHandle) {
          this.showLoadError();
          return;
        }

        try {
          const raw = await this.fetchAllProducts(shopDomain, storefrontToken, collectionHandle);
          this.products = raw.map((node) => ({
            handle: node.handle,
            title: node.title,
            vendor: node.vendor,
            subtitle: node.subtitle?.value,
            reviewRating: this.parseReviewRating(node.reviewRating?.value),
            reviewRatingCount: parseInt(node.reviewRatingCount?.value, 10) || 0,
            featuredImage: node.featuredImage,
            images: node.images.nodes,
            inputConnector: this.normalizeConnector(node.inputConnection?.value),
            outputConnector: this.normalizeConnector(node.outputConnection?.value),
            isBidirectional: node.cableBidirectional?.value === 'true',
            variants: node.variants.nodes.map((variant) => ({
              id: parseInt(variant.id.split('/').pop(), 10),
              title: variant.title,
              availableForSale: variant.availableForSale,
              price: variant.price,
              compareAtPrice: variant.compareAtPrice,
              image: variant.image,
            })),
          }));
          this.revealConnectors();
          this.loadingEl.classList.add('hidden');
          this.filterEl.classList.remove('hidden');
        } catch (err) {
          console.error('[cable-guide] Failed to load products:', err);
          this.showLoadError();
        }
      }

      async fetchAllProducts(domain, token, handle) {
        const endpoint = `https://${domain}/api/2026-01/graphql.json`;
        const headers = {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        };

        let products = [];
        let cursor = null;
        let hasNextPage = true;

        while (hasNextPage) {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify({ query: PRODUCTS_QUERY, variables: { handle, cursor } }),
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const { data, errors } = await response.json();
          if (errors?.length) throw new Error(errors[0].message);

          const page = data.collection?.products;
          if (!page) throw new Error('Cable collection was not found');

          products = products.concat(page.nodes);
          hasNextPage = page.pageInfo.hasNextPage;
          cursor = page.pageInfo.endCursor;
        }

        return products;
      }

      revealConnectors() {
        const inputSet = new Set();

        this.products.forEach(({ inputConnector, outputConnector, isBidirectional }) => {
          if (inputConnector) inputSet.add(inputConnector);
          if (isBidirectional) {
            if (outputConnector) inputSet.add(outputConnector);
          }
        });

        this.inputBtns.forEach((btn) => {
          const isAvailable = inputSet.has(this.normalizeConnector(btn.dataset.connector));
          btn.classList.remove('hidden');
          btn.classList.toggle('is-unavailable', !isAvailable);
          btn.disabled = !isAvailable;
        });
        this.outputBtns.forEach((btn) => {
          btn.classList.remove('hidden');
          btn.classList.add('is-unavailable');
          btn.disabled = true;
        });

        this.outputStep.setAttribute('aria-disabled', 'true');
      }

      selectInput(btn) {
        this.selectedInput = this.normalizeConnector(btn.dataset.connector);
        this.selectedOutput = null;

        this.inputBtns.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));

        const compatibleOutputs = new Set();
        this.products.forEach((product) => {
          this.getCompatibleOutputs(product, this.selectedInput).forEach((connector) => {
            compatibleOutputs.add(connector);
          });
        });

        this.outputBtns.forEach((outputBtn) => {
          const isCompatible = compatibleOutputs.has(this.normalizeConnector(outputBtn.dataset.connector));
          outputBtn.classList.remove('hidden');
          outputBtn.classList.toggle('is-unavailable', !isCompatible);
          outputBtn.disabled = !isCompatible;
          outputBtn.setAttribute('aria-pressed', 'false');
        });

        this.outputStep.setAttribute('aria-disabled', 'false');
        this.actionsEl.classList.remove('hidden');
        this.resultsEl.classList.add('hidden');
        this.directionalNoteEl.classList.add('hidden');
        this.productGrid.replaceChildren();
        this.errorEl.classList.add('hidden');

        this.outputStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      selectOutput(btn) {
        this.selectedOutput = this.normalizeConnector(btn.dataset.connector);
        this.outputBtns.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        this.showResults();
      }

      showResults() {
        const matching = this.products.filter((product) => this.productMatchesSelection(product));
        const cards = matching.flatMap((product) =>
          product.variants.map((variant) => this.renderCard(product, variant))
        );

        this.productGrid.innerHTML = cards.join('');
        this.resultsEl.classList.remove('hidden');
        this.noResultsEl.classList.toggle('hidden', cards.length > 0);
        this.productGrid.classList.toggle('hidden', cards.length === 0);
        this.directionalNoteEl.classList.toggle(
          'hidden',
          cards.length === 0 || !matching.some(({ isBidirectional }) => !isBidirectional)
        );

        this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      renderCard(product, variant) {
        const {
          handle,
          title: productTitle,
          vendor,
          subtitle,
          reviewRating,
          reviewRatingCount,
          featuredImage,
          images,
          isBidirectional,
        } = product;
        const {
          id: variantId,
          title: variantTitle,
          price,
          compareAtPrice,
          image,
          availableForSale,
        } = variant;
        const displayImage = image || featuredImage;
        const secondaryImage = images.find(({ url }) => url !== displayImage?.url);
        const showVariantTitle = variantTitle !== 'Default Title';
        const productUrl = `/products/${encodeURIComponent(handle)}?variant=${variantId}`;
        const ratioPercent = this.getRatioPercent(displayImage);
        const imageObjectClass = this.cardImageCover ? 'object-cover' : 'object-contain';
        const imagePositionClass = this.cardImagePosition === 'top' ? 'object-top' : '';
        const outerColorClass =
          this.cardStyle === 'card' ? ` color-${this.escapeAttr(this.cardColorScheme)} gradient` : '';
        const innerColorClass =
          this.cardStyle === 'standard'
            ? ` color-${this.escapeAttr(this.cardColorScheme)} gradient`
            : '';
        const innerRatioClass = this.cardImageRatio === 'adapt' ? '' : ' ratio';
        const imageHtml = this.renderCardImage(
          displayImage,
          productTitle,
          `card__image motion-reduce ${imageObjectClass} ${imagePositionClass}`
        );
        const secondaryImageHtml =
          this.cardShowSecondaryImage && secondaryImage
            ? this.renderCardImage(
                secondaryImage,
                productTitle,
                `card__image motion-reduce card__image-hover ${imageObjectClass} ${imagePositionClass}`
              )
            : '';
        const vendorHtml =
          this.cardShowVendor && vendor
            ? `<div class="product-vendor mb-1 caption-with-letter-spacing light">${this.escapeHtml(vendor)}</div>`
            : '';
        const variantHtml = showVariantTitle
          ? `<p class="${this.escapeAttr(this.cardTitleClass)} t-scale-075 mt--1 opacity-80">${this.escapeHtml(variantTitle)}</p>`
          : '';
        const subtitleHtml =
          this.cardShowSubtitle && subtitle
            ? `<p class="${this.escapeAttr(this.cardTitleClass)} t-scale-075 mt--1 opacity-80">${this.escapeHtml(subtitle)}</p>`
            : '';
        const ratingHtml = this.renderRating(reviewRating, reviewRatingCount);
        const quickAddHtml = this.cardEnableQuickAdd
          ? availableForSale
            ? `<div class="quick-add no-js-hidden">
                <button
                  type="button"
                  class="quick-add__submit button button--full-width button--secondary"
                  data-variant-id="${variantId}"
                  data-cable-atc
                >${this.escapeHtml(this.addToCartLabel)}</button>
              </div>`
            : `<div class="quick-add no-js-hidden">
                <button type="button" class="quick-add__submit button button--full-width button--secondary" disabled>
                  ${this.escapeHtml(this.soldOutLabel)}
                </button>
              </div>`
          : '';
        const isOnSale =
          compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);
        const saleCircleHtml = isOnSale
          ? `<div class="card__sale-circle" aria-hidden="true"><span>${this.escapeHtml(this.saleLabel)}</span></div>`
          : '';

        return `
          <li class="grid__item">
            <product-card class="block card-wrapper product-card-wrapper">
              <div
                class="product-card card card--${this.escapeAttr(this.cardStyle)} card--shape-${this.escapeAttr(this.cardImageRatio)} card--text-${this.escapeAttr(this.cardTextAlignment)} card--media${outerColorClass}"
                style="--ratio-percent: ${ratioPercent}%;"
                data-product-handle="${this.escapeAttr(handle)}"
              >
                <div
                  class="card__inner${innerColorClass}${innerRatioClass}"
                  style="--ratio-percent: ${ratioPercent}%;"
                >
                  <div class="card__media">
                    <div class="media media--transparent${secondaryImageHtml ? ' media--hover-effect' : ''}">
                      ${imageHtml}
                      ${secondaryImageHtml}
                    </div>
                  </div>
                  ${!isBidirectional ? `
                  <div class="card__content card__content--badge">
                    <div class="card__badge inline-flex gap-05 flex-wrap justify-start bottom-left">
                      <span class="badge badge--custom badge--bottom-left badge--${this.escapeAttr(this.badgeShape)}">
                        ${this.escapeHtml(this.directionalLabel)}
                      </span>
                    </div>
                  </div>` : ''}
                  ${saleCircleHtml}
                </div>
                <div class="card__content">
                  <div class="card__information">
                    ${vendorHtml}
                    <h3 class="card__heading text-body ${this.escapeAttr(this.cardTitleClass)}">
                      <a
                        href="${productUrl}"
                        class="full-unstyled-link ${this.escapeAttr(this.cardTitleTransform)}"
                        data-product-card-title-id="${variantId}"
                      >
                        ${this.escapeHtml(productTitle)}
                      </a>
                    </h3>
                    ${subtitleHtml}
                    ${variantHtml}
                    <div class="card-information">
                      ${ratingHtml}
                      ${this.renderPrice(price, compareAtPrice)}
                    </div>
                  </div>
                  ${quickAddHtml}
                </div>
              </div>
            </product-card>
          </li>
        `;
      }

      renderCardImage(image, productTitle, className) {
        if (!image) return '';

        return `<img
          srcset="${this.escapeAttr(image.url)} 720w"
          src="${this.escapeAttr(image.url)}"
          alt="${this.escapeAttr(image.altText || productTitle)}"
          class="${this.escapeAttr(className)}"
          loading="lazy"
          width="${image.width || 720}"
          height="${image.height || 720}"
        >`;
      }

      renderPrice(price, compareAtPrice) {
        const formattedPrice = this.formatPrice(price.amount, price.currencyCode);
        const isOnSale =
          compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);

        if (!isOnSale) {
          return `<div class="price ${this.escapeAttr(this.cardPriceClass)}">
            <div class="price__container">
              <div class="price__regular">
                <span class="visually-hidden">${this.escapeHtml(this.regularPriceLabel)}</span>
                <span class="price-item price-item--regular">${formattedPrice}</span>
              </div>
            </div>
          </div>`;
        }

        const formattedCompareAtPrice = this.formatPrice(
          compareAtPrice.amount,
          compareAtPrice.currencyCode
        );

        return `<div class="price price--on-sale ${this.escapeAttr(this.cardPriceClass)}">
          <div class="price__container">
            <div class="price__sale">
              <span class="visually-hidden">${this.escapeHtml(this.regularPriceLabel)}</span>
              <s class="price-item price-item--regular">${formattedCompareAtPrice}</s>
              <span class="visually-hidden">${this.escapeHtml(this.salePriceLabel)}</span>
              <span class="price-item price-item--sale price-item--last">${formattedPrice}</span>
            </div>
          </div>
        </div>`;
      }

      renderRating(reviewRating, reviewRatingCount) {
        if (!this.cardShowRating || !reviewRating) return '';

        const decimal = reviewRating.rating % 1;
        let ratingDecimal = 0;
        if (decimal >= 0.3 && decimal <= 0.7) ratingDecimal = 0.5;
        if (decimal > 0.7) ratingDecimal = 1;

        return `<div class="card-rating-container flex items-center mt-05 gap-05 justify-${this.escapeAttr(this.cardTextAlignment)}">
          <div
            class="rating"
            role="img"
            aria-label="${reviewRating.rating} / ${reviewRating.scaleMax}"
          >
            <span
              aria-hidden="true"
              class="rating-star"
              style="--rating: ${Math.floor(reviewRating.rating)}; --rating-max: ${reviewRating.scaleMax}; --rating-decimal: ${ratingDecimal}; --font-size: ${this.cardStarSize}"
            ></span>
          </div>
          <p class="rating-text caption visually-hidden">
            <span aria-hidden="true">${reviewRating.rating} / ${reviewRating.scaleMax}</span>
          </p>
          <p class="rating-count caption">
            <span aria-hidden="true">(${reviewRatingCount})</span>
            <span class="visually-hidden">${reviewRatingCount}</span>
          </p>
        </div>`;
      }

      parseReviewRating(value) {
        if (!value) return null;

        let ratingValue;
        try {
          ratingValue = JSON.parse(value);
        } catch (err) {
          console.error('[cable-guide] Invalid review rating metafield:', err);
          return null;
        }

        const rating = parseFloat(ratingValue.value);
        const scaleMax = parseFloat(ratingValue.scale_max);
        if (!Number.isFinite(rating) || !Number.isFinite(scaleMax)) return null;

        return { rating, scaleMax };
      }

      getRatioPercent(image) {
        const ratios = {
          square: 1,
          portrait: 0.8,
          portrait_tall: 0.66,
          landscape: 1.33,
          landscape_wide: 1.77,
        };
        const ratio =
          ratios[this.cardImageRatio] ||
          (image?.width && image?.height ? image.width / image.height : 1);

        return 100 / ratio;
      }

      async addToCart(button) {
        const variantId = button.dataset.variantId;
        const originalText = button.textContent.trim();
        button.disabled = true;

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: parseInt(variantId, 10),
              quantity: 1,
              sections: ['cart-drawer', 'cart-icon-bubble'],
            }),
          });

          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || response.statusText);
          }

          const data = await response.json();
          const cartDrawer = document.querySelector('cart-drawer');

          if (cartDrawer) {
            cartDrawer.init?.();
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents?.(data);
          }

          button.textContent = this.addedLabel;
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 1500);
        } catch (err) {
          console.error('[cable-guide] Cart add failed:', err);
          button.textContent = this.cartErrorLabel;
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        }
      }

      getCompatibleOutputs(product, selectedInput) {
        const outputs = [];

        if (product.inputConnector === selectedInput && product.outputConnector) {
          outputs.push(product.outputConnector);
        }

        if (
          product.isBidirectional &&
          product.outputConnector === selectedInput &&
          product.inputConnector
        ) {
          outputs.push(product.inputConnector);
        }

        return outputs;
      }

      productMatchesSelection(product) {
        const matchesForward =
          product.inputConnector === this.selectedInput &&
          product.outputConnector === this.selectedOutput;
        const matchesReverse =
          product.isBidirectional &&
          product.outputConnector === this.selectedInput &&
          product.inputConnector === this.selectedOutput;

        return matchesForward || matchesReverse;
      }

      reset() {
        this.selectedInput = null;
        this.selectedOutput = null;

        this.inputBtns.forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
        this.outputBtns.forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
        this.revealConnectors();

        this.actionsEl.classList.add('hidden');
        this.resultsEl.classList.add('hidden');
        this.directionalNoteEl.classList.add('hidden');
        this.productGrid.replaceChildren();
        this.errorEl.classList.add('hidden');
      }

      showLoadError() {
        this.loadingEl.classList.add('hidden');
        this.errorEl.classList.remove('hidden');
      }

      normalizeConnector(value) {
        return value?.trim().toLowerCase() || null;
      }

      formatPrice(amount, currencyCode) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 2,
        }).format(parseFloat(amount));
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      }

      escapeAttr(value) {
        return this.escapeHtml(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      }

    }
  );
}
